import { X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'

import { Button } from '@/components/ui'
import { cn } from '@/utils'

interface BulkActionsToolbarProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof motion.div>,
    'ref' | 'children'
  > {
  selectedCount?: number
  onClose?: () => void
  children?: React.ReactNode
}

const BulkActionsToolbar = React.forwardRef<
  HTMLDivElement,
  BulkActionsToolbarProps
>(({ selectedCount = 0, onClose, children, className, ...props }, ref) => {
  const isOpen = selectedCount > 0

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={ref}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300,
            mass: 0.8,
          }}
          className={cn(
            'fixed bottom-4 left-1/2 -translate-x-1/2 z-50',
            'flex items-center gap-4 px-6 py-3.5',
            'bg-white border border-border rounded-lg',
            'shadow-lg',
            className,
          )}
          {...props}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-7">
              {selectedCount} {selectedCount === 1 ? 'record' : 'records'}{' '}
              selected
            </span>
          </div>

          <div className="h-6 w-px bg-border" />

          <div className="flex items-center gap-2">{children}</div>

          <div className="h-6 w-px bg-border" />

          <Button
            theme="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 hover:bg-neutral-100"
            aria-label="Close bulk actions"
          >
            <X className="h-4 w-4" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
})
BulkActionsToolbar.displayName = 'BulkActionsToolbar'

export { BulkActionsToolbar }
